/**
 * Os desenhos do menu.
 *
 * <p>São SVG, e não glifos de fonte, porque o que o menu precisa dizer não existe em
 * Unicode: uma cabeça élfica de orelhas pontudas, um felino, uma poção. Os símbolos
 * que havia antes eram aproximações — ❦ para criatura, ✦ para item — e aproximação em
 * ícone é o mesmo que legenda: só funciona para quem já sabe o que aquilo quer dizer.
 *
 * <p>Emoji resolveria o repertório e quebraria o resto: emoji é colorido por conta
 * própria, ignora {@code color}, e não sabe ficar escuro sobre a pastilha da cor da
 * seção. Estes herdam a cor de quem os contém, como o resto do design system.
 *
 * <p>Mesma convenção do {@code IconeDeRegiao}: caixa 24×24, traço de 1.7, pontas
 * redondas, silhueta simples. Eles aparecem a 20px — detalhe fino some nesse tamanho.
 */
interface Props {
  icone: string;
  tamanho?: number;
  titulo?: string;
}

export function IconeDoMenu({ icone, tamanho = 20, titulo }: Props) {
  return (
    <svg
      width={tamanho}
      height={tamanho}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
      role="img"
      aria-label={titulo ?? icone}
    >
      {titulo && <title>{titulo}</title>}
      {desenho(icone)}
    </svg>
  );
}

function desenho(icone: string) {
  switch (icone) {
    /* --------------------------------------------------- gestão de projeto */

    // Mapa dobrado: o roadmap é o mapa do que o time vai percorrer.
    case 'mapa':
      return (
        <>
          <path d="M3 6.5l6-3 6 3 6-3v14l-6 3-6-3-6 3z" />
          <path d="M9 3.5v14M15 6.5v14" />
        </>
      );

    // Três colunas de alturas diferentes: o quadro é o trabalho em pilhas, e a pilha
    // desigual é o que se lê nele de relance — onde está entalando.
    case 'quadro':
      return (
        <>
          <rect x="3" y="4" width="5" height="16" rx="1.5" />
          <rect x="9.5" y="4" width="5" height="10" rx="1.5" />
          <rect x="16" y="4" width="5" height="13" rx="1.5" />
        </>
      );

    // Duas pessoas: a equipe é gente, no plural — o NPC é que é uma só.
    case 'pessoas':
      return (
        <>
          <circle cx="9.5" cy="8" r="3.2" />
          <path d="M3.5 19.5c0-3.3 2.7-5 6-5s6 1.7 6 5" />
          <path d="M16.5 5.4a3.2 3.2 0 0 1 0 5.6" />
          <path d="M18 19.5c0-2.3-.8-3.8-2-4.6 3-.4 5.5 1.4 5.5 4.6" />
        </>
      );

    /* ------------------------------------------------ marketing e branding */

    case 'olho':
      return (
        <>
          <path d="M2.5 12s3.6-6 9.5-6 9.5 6 9.5 6-3.6 6-9.5 6-9.5-6-9.5-6z" />
          <circle cx="12" cy="12" r="2.7" />
        </>
      );

    // Espada: a campanha é o que se lança.
    //
    // Em pé e preenchida, depois que a diagonal contornada não passou no teste: a
    // 36px ela virava uma flecha, porque a guarda e o punho eram traços finos que o
    // olho não separava da lâmina. Cheia e vertical, as três partes se distinguem.
    case 'espada':
      return (
        <g fill="currentColor" stroke="none">
          {/* A proporção é tudo aqui. Lâmina grossa vira pino; lâmina fina demais
              contra guarda larga vira sinal de mais. A lâmina afila da guarda até a
              ponta, que é o que faz o olho ler "gume" e não "traço". */}
          <path d="M12 1.6l1.9 4.3-.35 7.3h-3.1L10.1 5.9z" />
          <rect x="6.7" y="13.2" width="10.6" height="2.2" rx="1.1" />
          <rect x="10.8" y="15.4" width="2.4" height="4.1" />
          <circle cx="12" cy="20.7" r="1.7" />
        </g>
      );

    /* ----------------------------------------------------- criação do mundo */

    // A ilha: a terra cheia sobre a água, com a palmeira que a marca de longe.
    // O morro é maciço para separar terra de mar sem depender de um fio de contorno.
    case 'ilha':
      return (
        <>
          <path
            d="M3.4 16.8c1.8-4.4 4.6-6.6 8.6-6.6s6.8 2.2 8.6 6.6z"
            fill="currentColor"
            stroke="none"
          />
          <path d="M12 10.4V6" strokeWidth={2} />
          <path d="M12 6c-2-1.5-4-1-4.6.6M12 6c2-1.5 4-1 4.6.6" strokeWidth={1.9} />
          <path d="M2.6 20.2c1.6 0 1.6 1.3 3.2 1.3s1.6-1.3 3.2-1.3 1.6 1.3 3.2 1.3 1.6-1.3 3.2-1.3 1.6 1.3 3.2 1.3 1.6-1.3 2.8-1.3" />
        </>
      );

    // Sol. É o mesmo desenho que uma região pode adotar em IconeDeRegiao — de
    // propósito: dois sóis diferentes no mesmo produto seriam duas coisas para o olho,
    // e é um só. Se algum dia divergirem, é sinal de que um dos dois foi editado sem
    // olhar o outro.
    case 'sol':
      return (
        <>
          <circle cx="12" cy="12" r="4.5" />
          <path d="M12 2v2.5M12 19.5V22M2 12h2.5M19.5 12H22M4.9 4.9l1.8 1.8M17.3 17.3l1.8 1.8M19.1 4.9l-1.8 1.8M6.7 17.3l-1.8 1.8" />
        </>
      );

    // Folha de calendário: as orelhas da argola em cima, a faixa do cabeçalho e um
    // dia marcado — o Calendário da Season.
    case 'calendario':
      return (
        <>
          <rect x="3.5" y="5" width="17" height="15.5" rx="2" />
          <path d="M3.5 9.5h17M8 2.5V6M16 2.5V6" />
          <rect x="7" y="12.5" width="3.4" height="3.4" rx="0.8" fill="currentColor" stroke="none" />
        </>
      );

    // O antigo ícone do Mapa da Ilha (◉), que desce um degrau: a área é o alvo
    // marcado sobre o mapa.
    case 'alvo':
      return (
        <>
          <circle cx="12" cy="12" r="8.2" />
          <circle cx="12" cy="12" r="2.6" fill="currentColor" stroke="none" />
        </>
      );

    // O antigo ícone de Regiões e Áreas (◆), que desce mais um: o local é o ponto.
    case 'losango':
      return <path d="M12 3l7.5 9-7.5 9-7.5-9z" />;

    /* ------------------------------------------------------ banco de dados */

    // Cabeça élfica: a orelha pontuda é o que distingue um povo de uma pessoa.
    //
    // A cabeça volta a ser contorno, e só as orelhas ficam cheias. Preenchendo tudo,
    // cabeça e orelhas fundiam num corpo só — e a silhueta que sobrava era um cálice,
    // não um elfo. O contorno devolve o rosto; a massa fica onde ela informa.
    case 'elfo':
      return (
        <>
          <circle cx="12" cy="8.8" r="4.1" strokeWidth={1.9} />
          <g fill="currentColor" stroke="none">
            <path d="M8.3 7.2L4.1 4.1l1.2 5.2zM15.7 7.2l4.2-3.1-1.2 5.2z" />
          </g>
          <path d="M5.2 20.8c0-3.4 3-5.4 6.8-5.4s6.8 2 6.8 5.4" strokeWidth={1.9} />
        </>
      );

    // Estandarte: o mesmo ⚑ de antes, agora desenhado.
    case 'estandarte':
      return (
        <>
          <path d="M6 21.5V3" />
          <path d="M6 4h11.5l-2.7 3.7L17.5 11.5H6" />
        </>
      );

    // Uma pessoa só, de frente: o NPC. A Equipe é o plural disto.
    case 'personagem':
      return (
        <>
          <circle cx="12" cy="8" r="3.6" />
          <path d="M5 20.5c0-3.9 3.1-6.2 7-6.2s7 2.3 7 6.2" />
        </>
      );

    // Felino inteiro, de perfil.
    //
    // Preenchido, e não contornado, porque a primeira versão falhou exatamente onde
    // este ícone precisa funcionar: a 36px, quatro patas de traço fino e um dorso
    // aberto viravam uma forma indistinta. Cheia, a silhueta diz "bicho de quatro
    // patas com rabo" antes de dizer qual.
    case 'felino':
      return (
        <>
          <g fill="currentColor" stroke="none">
            {/* cabeça e as duas orelhas */}
            <circle cx="6.1" cy="9.4" r="2.7" />
            <path d="M3.9 7.4L3 4.5l2.9 1.6zM8.3 7.4L9.2 4.5 6.3 6.1z" />
            {/* tronco */}
            <rect x="7" y="7.6" width="11.6" height="5.6" rx="2.8" />
            {/* as patas, uma de cada par — de perfil, a de trás fica escondida */}
            <rect x="8.4" y="12" width="2.3" height="7.4" rx="1.15" />
            <rect x="15.4" y="12" width="2.3" height="7.4" rx="1.15" />
          </g>
          {/* a cauda continua traçada: é a única parte que precisa de curva fina */}
          <path d="M18.4 9.3c2.3-.3 3.4-1.8 3.3-4.4" strokeWidth={2} />
        </>
      );

    // Poção: o item é o que se pega, se bebe e se fabrica.
    case 'pocao':
      return (
        <>
          <path d="M9.8 3h4.4" />
          <path d="M10.4 3v4.8l-3.7 6.6a4.6 4.6 0 0 0 4 6.9h2.6a4.6 4.6 0 0 0 4-6.9l-3.7-6.6V3" />
          <path d="M7.4 14.2h9.2" />
        </>
      );

    // O antigo ícone de NPCs (✤): a quest é a flor de quatro pontas do mapa.
    case 'rosacea':
      return <path d="M12 5.6c1.6 3.2 3.2 4.8 6.4 6.4-3.2 1.6-4.8 3.2-6.4 6.4-1.6-3.2-3.2-4.8-6.4-6.4 3.2-1.6 4.8-3.2 6.4-6.4z" />;

    // Um dado de vinte faces, que e o que uma mesa poe no centro. As fichas do Mistik
    // Dream sao a unica tela do produto que serve a uma partida, e nao ao projeto.
    case 'dado':
      return (
        <>
          <path d="M12 2.6l8.2 4.7v9.4L12 21.4 3.8 16.7V7.3z" />
          <path d="M12 2.6v8.2l8.2-3.5M12 10.8L3.8 7.3M12 10.8v10.6" />
        </>
      );

    // Uma cena que se abre em duas: o motor do jogo é uma árvore de cenas ligadas por
    // escolhas, e o que a página resume é exatamente a bifurcação — a ação que leva a
    // um lugar ou a outro. Os nós são cheios para o desenho não virar três círculos
    // vazios a 20px.
    case 'fluxo':
      return (
        <>
          <circle cx="6" cy="12" r="2.6" fill="currentColor" stroke="none" />
          <circle cx="18" cy="6.2" r="2.6" fill="currentColor" stroke="none" />
          <circle cx="18" cy="17.8" r="2.6" fill="currentColor" stroke="none" />
          <path d="M8.4 10.9L15.6 7.3M8.4 13.1l7.2 3.6" />
        </>
      );

    // O antigo ícone de Quests (❖): o evento é o losango que se abre.
    case 'losango-duplo':
      return (
        <>
          <path d="M12 2.5L20 12l-8 9.5L4 12z" />
          <path d="M12 8l4 4-4 4-4-4z" />
        </>
      );

    // Carta: o retângulo do baralho com o naipe no meio. O pipe cheio é o que separa
    // a carta de uma folha de papel a 20px — sem ele, sobra um retângulo.
    case 'carta':
      return (
        <>
          <rect x="6.4" y="3" width="11.2" height="18" rx="2.4" />
          <path d="M12 8.2l2.7 3.8-2.7 3.8-2.7-3.8z" fill="currentColor" stroke="none" />
        </>
      );

    /* ------------------------------------------------------------ documentos */

    // Folha com a ponta dobrada e as linhas do texto: o documento escrito.
    case 'documento':
      return (
        <>
          <path d="M14 2.8H7.2A2.2 2.2 0 0 0 5 5v14a2.2 2.2 0 0 0 2.2 2.2h9.6A2.2 2.2 0 0 0 19 19V7.8z" />
          <path d="M14 2.8V7.8H19" />
          <path d="M8.6 12.4h6.8M8.6 15.6h6.8M8.6 9.2h2.6" />
        </>
      );

    case 'imagem':
      return (
        <>
          <rect x="3.5" y="4" width="17" height="16" rx="2" />
          <circle cx="8.5" cy="9" r="1.5" />
          <path d="M4.5 18l5-5 3.3 3 2.4-2.4 4.3 4.4" />
        </>
      );

    // Chave deitada: os Acessos são literalmente as chaves dos ambientes.
    case 'chave':
      return (
        <>
          <circle cx="7.5" cy="12" r="3.6" />
          <path d="M11.1 12h9.4" />
          <path d="M17 12v3.2M20.5 12v2.4" />
        </>
      );

    // Balões de fala empilhados: a reunião é gente falando, e o arquivo é o registro
    // disso. Um microfone diria "gravar", que é o passo anterior e acontece fora daqui.
    case 'transcricao':
      return (
        <>
          <path d="M4 6.2a2 2 0 0 1 2-2h8.4a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2H8.6L5 16.2V13.2a2 2 0 0 1-1-1.7z" />
          <path d="M8.4 7.6h5.6M8.4 10.2h3.4" />
          <path d="M18.2 9.4h.2a2 2 0 0 1 2 2v4.4a2 2 0 0 1-1 1.7v2.7l-2.8-2.6" />
        </>
      );

    /* -------------------------------------------------------------- progresso */

    // Três barras subindo, uma delas ainda curta: é literalmente o que a tela mostra,
    // e a que falta é o assunto dela. Uma ampulheta diria "espere", que é o contrário.
    case 'barras':
      return (
        <>
          <path d="M4 20.2h16" />
          <path d="M7 20.2V13M12 20.2V8M17 20.2v-4" />
        </>
      );

    /* ------------------------------------------ as barras do Progresso */
    //
    // Estes quatro não estão no menu: existem para o painel de Progresso, onde cada
    // coleção precisa de um desenho próprio. Sem eles, as cinco barras de item
    // ficariam com o mesmo frasco de poção, que é o mesmo que não ter ícone nenhum.

    // Hexágono com visto: a área é um hexágono no mapa, e "completa" é o visto dela.
    //
    // Era uma bandeira, e a bandeira já era o estandarte da Facção — duas linhas
    // vizinhas do painel ficavam com o mesmo desenho, que é o mesmo que nenhum.
    case 'hexagono-feito':
      return (
        <>
          <path d="M12 2.6l8.2 4.7v9.4L12 21.4l-8.2-4.7V7.3z" />
          <path d="M8.4 12l2.6 2.6 4.8-5" />
        </>
      );

    // Bigorna: o que se fabrica.
    case 'bigorna':
      return (
        <>
          <path d="M3.2 8.6h9.4l2.6 2.6h5.6c0 2.6-2.2 4.4-4.8 4.4H8.6c-3 0-5.4-2.2-5.4-5.2z" />
          <path d="M9 15.6l-1.4 4.6h8.8l-1.4-4.6" />
        </>
      );

    // Picareta: o que se leva para coletar. Não é a bigorna — aquela é onde se
    // fabrica, esta é o que se carrega para trabalhar.
    case 'picareta':
      return (
        <>
          <path d="M4.4 9.2c3-4.4 12.2-4.4 15.2 0" />
          <path d="M13 5.2L10.2 20.8" />
        </>
      );

    // Saco amarrado: o que cai do inimigo.
    case 'saco':
      return (
        <>
          <path d="M9.4 6.4h5.2c2.4 2 4 4.8 4 8 0 3.4-2.8 5.8-6.6 5.8s-6.6-2.4-6.6-5.8c0-3.2 1.6-6 4-8z" />
          <path d="M9 6.4l1.2-2.6h3.6L15 6.4" />
        </>
      );

    // Baú de tampa arredondada: o que só vale dinheiro. O saco do dropável é mole e
    // sem fechadura; este é rígido e trancado — é a diferença que se vê a 20px.
    case 'bau':
      return (
        <>
          <path d="M3.4 10.4h17.2v9.2H3.4z" />
          <path d="M3.4 10.4c0-3.4 3.8-5.8 8.6-5.8s8.6 2.4 8.6 5.8" />
          <path d="M10.6 12.6h2.8v3.2h-2.8z" />
        </>
      );

    // Chave: o item que só vale enquanto a quest corre. A rosácea já é a Quest em si,
    // e repetir o desenho apagaria justamente a distinção entre a quest e o seu item.
    case 'chave':
      return (
        <>
          <circle cx="8.6" cy="8.6" r="3.9" />
          <path d="M11.4 11.4l7.2 7.2" />
          <path d="M15.6 13.8l-1.9 1.9" />
        </>
      );

    default:
      return <circle cx="12" cy="12" r="7" />;
  }
}
